import { Link } from "react-router";

import { NavigationArtifact } from "@/assets/background-patterns/navigation-artifact";
import { CompanyLogo } from "@/assets/logos/company-logo";
import { ROUTES } from "@/constants/routes";
import { SensorStatusCard } from "@/features/events/components/status/sensor-status-card";

import type { NavItemDividerType, NavItemType } from "../config";

import { MobileNavigationHeader } from "../components/mobile-header";
import { NavAccountCard } from "../components/nav-account-card";
import { NavList } from "../components/nav-list";

interface SidebarNavigationSectionDividersProps {
	/** URL of the currently active item. */
	activeUrl?: string;
	/** List of items to display. */
	items: (NavItemDividerType | NavItemType)[];
}

export const SidebarNavigationSectionDividers = ({
	activeUrl,
	items,
}: SidebarNavigationSectionDividersProps) => {
	// Render a fresh tree for mobile vs desktop so each gets its own
	// NavigationArtifact instance (unique gradient ids) and React doesn’t
	// move a single element between two parents.
	const renderContent = () => (
		<aside className="relative flex h-full min-h-dvh w-[var(--width-main-sidebar)] min-w-65 flex-col justify-between pt-4 lg:min-h-0 lg:pt-5">
			{/* Scroll the nav content, not the aside — overflow on the aside
			    isolates blend modes from the drawer/sidebar backdrop. */}
			<div className="relative z-10 flex min-h-0 flex-1 flex-col justify-between overflow-auto">
				<div className="flex flex-col gap-5 px-4">
					<Link to={ROUTES.DASHBOARD}>
						<CompanyLogo className="h-8 w-32" />
					</Link>
				</div>

				<NavList activeUrl={activeUrl} className="mt-5" items={items} />

				<div className="mt-auto flex flex-col gap-4 px-4 py-4">
					<SensorStatusCard />
					<NavAccountCard />
				</div>
			</div>
			<NavigationArtifact />
		</aside>
	);

	return (
		<>
			{/* Mobile header navigation */}
			<MobileNavigationHeader>{renderContent()}</MobileNavigationHeader>

			{/* Desktop sidebar navigation */}
			<div
				className="hidden lg:fixed lg:bottom-0 lg:left-0 lg:flex"
				style={{ top: "var(--sensor-banner-height, 0px)" }}
			>
				{renderContent()}
			</div>

			{/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
			<div
				className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
				style={{
					paddingLeft: "calc(var(--width-main-sidebar))", // Add 4px to account for the padding in the sidebar wrapper
				}}
			/>
		</>
	);
};
